import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_URL, TWITTER_HANDLE, DEFAULT_OG_IMAGE } from '../../constants/seo';

/**
 * SEO component – per-page title, meta, Open Graph, Twitter, canonical, JSON-LD, geo
 * @param {string} title - Page title (defaults to site name)
 * @param {string} description - Meta description
 * @param {string} canonical - Canonical URL (absolute or path; path is prefixed with SITE_URL)
 * @param {string} image - OG/Twitter image URL (absolute)
 * @param {string} keywords - Optional meta keywords (comma-separated)
 * @param {boolean} noindex - If true, adds robots noindex,nofollow
 * @param {object} jsonLd - Optional JSON-LD object(s). If array, multiple scripts; if object, single script
 * @param {object} geo - Optional geo targeting { region, placename, position, icbm, country }
 * @param {string} locale - og:locale (e.g. fr_FR)
 * @param {string} type - og:type (website, article, etc.)
 */
function SEO({
  title,
  description,
  canonical,
  image = DEFAULT_OG_IMAGE,
  keywords,
  noindex = false,
  jsonLd,
  geo,
  locale = 'fr_FR',
  type = 'website',
}) {
  const canonicalUrl = canonical?.startsWith('http') ? canonical : canonical ? `${SITE_URL}${canonical.startsWith('/') ? canonical : `/${canonical}`}` : SITE_URL + '/';
  const imageUrl = image?.startsWith('http') ? image : `${SITE_URL}${image?.startsWith('/') ? image : '/logo512.png'}`;

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Geo targeting (local SEO) */}
      {geo?.region && <meta name="geo.region" content={geo.region} />}
      {geo?.country && <meta name="geo.country" content={geo.country} />}
      {geo?.placename && <meta name="geo.placename" content={geo.placename} />}
      {geo?.icbm && <meta name="ICBM" content={geo.icbm} />}
      {geo?.position && <meta name="geo.position" content={geo.position} />}
      {geo && <meta name="format-detection" content="telephone=yes" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content="Synaptek" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content={TWITTER_HANDLE} />

      {/* JSON-LD */}
      {jsonLd && (
        Array.isArray(jsonLd)
          ? jsonLd.map((item, i) => (
              <script key={i} type="application/ld+json">
                {JSON.stringify(item)}
              </script>
            ))
          : (
              <script type="application/ld+json">
                {JSON.stringify(jsonLd)}
              </script>
            )
      )}
    </Helmet>
  );
}

export default SEO;
